/**
 * legalProceduresSlice — Spawning des dossiers juridiques structurés.
 *
 * Évalue chaque jour les templates de `data/legalProcedureTemplates.js` :
 *   - filtre selon condition + cooldown
 *   - tire selon probabilité
 *   - pousse un message kind='decision' avec senderType='legal'
 *
 * État :
 *   - legalProcedureCooldowns : { templateId: lastSpawnDay }
 *   - legalProceduresHistory  : log des procédures résolues (cap 50)
 *
 * 17/06/2026 — Création (Lot 6 Phase L2).
 */

import { LEGAL_PROCEDURE_TEMPLATES } from '../../data/legalProcedureTemplates';

/** Limite à 1 spawn / jour pour éviter la noyade de boîte juridique. */
const MAX_SPAWN_PER_TICK = 1;

export const createLegalProceduresSlice = (set, get) => ({
  /** Cooldowns par template — empêche le re-spawn rapide. */
  legalProcedureCooldowns: {},
  /** Historique des procédures résolues (pour le bilan annuel Juliette). */
  legalProceduresHistory: [],

  /**
   * Appelé chaque jour depuis nextDay. Évalue les templates, tire et
   * éventuellement spawn un dossier juridique.
   *
   * @returns {{ spawned: number, evaluated: number }}
   */
  legalProceduresTick: () => {
    const state = get();
    if (typeof get().pushCustomMessage !== 'function') return { spawned: 0, evaluated: 0 };
    const today = state.totalDays || 0;
    const cooldowns = state.legalProcedureCooldowns || {};
    let evaluated = 0;
    const candidates = [];

    for (const tpl of LEGAL_PROCEDURE_TEMPLATES) {
      evaluated += 1;
      const lastFired = cooldowns[tpl.id] || 0;
      // 17/06/2026 — Cooldown ne bloque QUE si déjà tiré au moins une fois
      // (sinon lastFired=0 au démarrage bloquerait tout pendant cooldownDays).
      if (lastFired > 0 && today - lastFired < tpl.cooldownDays) continue;
      try {
        if (!tpl.condition(state)) continue;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[legalProcedures] condition failed', tpl.id, err);
        continue;
      }
      if (Math.random() > tpl.probability) continue;
      candidates.push(tpl);
    }

    const toSpawn = candidates.slice(0, MAX_SPAWN_PER_TICK);
    const newCooldowns = { ...cooldowns };
    let spawned = 0;

    toSpawn.forEach((tpl) => {
      try {
        const msgPayload = tpl.build(state);
        get().pushCustomMessage({
          ...msgPayload,
          templateId: tpl.id,
          payload: { ...(msgPayload.payload || {}), templateId: tpl.id, eventType: tpl.type },
        });
        newCooldowns[tpl.id] = today;
        spawned += 1;
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.debug(`[legalProcedures] spawned ${tpl.id} on day ${today}`);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[legalProcedures] build failed', tpl.id, err);
      }
    });

    if (spawned > 0) {
      set({ legalProcedureCooldowns: newCooldowns });
    }
    return { spawned, evaluated };
  },

  /**
   * Enregistre la résolution d'une procédure juridique dans l'historique
   * (utilisé par le bilan annuel Juliette en Phase ultérieure et par les
   * stats du joueur). Appelé par messageSlice à chaque décision sur un
   * message senderType='legal'.
   *
   * @param {{ templateId: string, choiceId: string, day: number, cost: number }} entry
   */
  recordLegalProcedureResolution: (entry) => {
    if (!entry || !entry.templateId) return;
    const history = get().legalProceduresHistory || [];
    const next = [
      ...history.slice(-49), // cap 50
      {
        templateId: entry.templateId,
        choiceId: entry.choiceId || 'unknown',
        day: entry.day ?? get().totalDays ?? 0,
        cost: entry.cost ?? 0,
      },
    ];
    set({ legalProceduresHistory: next });
  },
});
